import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, ShoppingCart, CreditCard, Globe, IndianRupee, Copy, Check, ExternalLink, CheckCircle2, AlertCircle, Clock, Info, Maximize2, X } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;

// Payment configuration
const PAYMENT_CONFIG = {
  venmo: {
    handle: '@SurfaceArea-Project',
    displayName: 'SurfaceArea Project',
  },
  paypal: {
    email: 'payments@surfacearea.shop',
    link: 'https://paypal.me/SurfaceAreaProject',
  },
  zelle: {
    email: 'payments@surfacearea.shop',
  },
  razorpay: {
    link: 'https://pages.razorpay.com/pl_Sdo0L7nee1ZXnA/view',
  },
};

const PLATFORM_FEE = 20;
const PLATFORM_FEE_THRESHOLD = 100;
const USD_TO_INR_FALLBACK = 90;

function getTimeAgo(dateStr) {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

export default function StartupCheckout() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [checkoutData, setCheckoutData] = useState(null);
  const [isIndia, setIsIndia] = useState(null); // null = not chosen yet
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState(null);
  const [paymentStatus, setPaymentStatus] = useState('pending');
  const [paymentTab, setPaymentTab] = useState('venmo');
  const [error, setError] = useState(null);
  const [usdToInr, setUsdToInr] = useState(USD_TO_INR_FALLBACK);
  const [lightboxSrc, setLightboxSrc] = useState(null);
  const [expiryCountdown, setExpiryCountdown] = useState(null); // { mins, secs } or null
  const expiryRef = useRef(null);

  const loadCheckout = useCallback(async () => {
    setLoading(true);
    try {
      const [res, rateRes] = await Promise.allSettled([
        axios.get(`${API}/startup/checkout`, { withCredentials: true }),
        axios.get(`${API}/exchange-rate`),
      ]);

      if (res.status === 'rejected') {
        throw res.reason;
      }
      setCheckoutData(res.value.data);

      // Live exchange rate (proxied through backend)
      if (rateRes.status === 'fulfilled' && rateRes.value.data?.inr) {
        setUsdToInr(rateRes.value.data.inr);
      }

      // Auto-detect India from email domain
      const domain = res.value.data?.email_domain || '';
      if (domain.endsWith('.in')) {
        setIsIndia(true);
      }
      // Check if already submitted
      if (res.value.data?.existing_payment) {
        setSubmitted(true);
        setPaymentMethod(res.value.data.existing_payment.payment_method);
        setPaymentStatus(res.value.data.existing_payment.status || 'pending');
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load checkout');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadCheckout(); }, [loadCheckout]);

  // Poll for payment status changes when in pending state
  useEffect(() => {
    if (!submitted || paymentStatus !== 'pending') return;
    const interval = setInterval(async () => {
      try {
        const res = await axios.get(`${API}/startup/checkout`, { withCredentials: true });
        if (res.data?.existing_payment) {
          const newStatus = res.data.existing_payment.status || 'pending';
          if (newStatus !== 'pending') {
            setPaymentStatus(newStatus);
            setCheckoutData(res.data);
          }
        }
      } catch { }
    }, 10000); // poll every 10 seconds
    return () => clearInterval(interval);
  }, [submitted, paymentStatus]);

  const handleSubmitPayment = async (method) => {
    if (!checkoutData?.acceptances?.length) return;
    setSubmitting(true);
    try {
      const acceptanceIds = checkoutData.acceptances.map(a => a.acceptance_id);
      await axios.post(`${API}/startup/checkout/complete`, {
        payment_method: method,
        acceptance_ids: acceptanceIds,
      }, { withCredentials: true });
      setSubmitted(true);
      setPaymentMethod(method);
      setPaymentStatus('pending');
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to record payment');
    } finally {
      setSubmitting(false);
    }
  };

  const getLogoUrl = (path) => {
    if (!path) return null;
    if (path.startsWith('http')) return path;
    return `${BACKEND}/api/files/${path}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#F6F1E8' }}>
        <div className="animate-spin w-8 h-8 border-4 border-[#121212] border-t-transparent rounded-full" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#F6F1E8' }}>
        <div className="bg-[#EDE3C7] border-[3px] border-[#121212] shadow-[6px_6px_0px_0px_#121212] rounded-2xl p-8 text-center max-w-md">
          <AlertCircle className="w-12 h-12 mx-auto mb-4" strokeWidth={2} style={{ color: '#FF6B6B' }} />
          <p className="font-bold text-lg mb-2">{error}</p>
          <button
            onClick={() => navigate('/dashboard/startup')}
            className="bg-[#FF6B6B] text-[#121212] font-black uppercase text-sm border-[3px] border-[#121212] shadow-[4px_4px_0px_0px_#121212] hover:-translate-y-[2px] hover:shadow-[6px_6px_0px_0px_#121212] active:translate-y-[4px] active:shadow-none transition-all rounded-xl px-6 py-3 mt-4"
            data-testid="back-to-dashboard-btn"
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  const acceptances = checkoutData?.acceptances || [];
  const totalCost = checkoutData?.total_cost || 0;
  const platformFee = totalCost > PLATFORM_FEE_THRESHOLD ? PLATFORM_FEE : 0;
  const grandTotal = totalCost + platformFee;
  const inrGrandTotal = Math.round(grandTotal * usdToInr);

  // Pending verification state
  if (submitted) {
    const isConfirmed = paymentStatus === 'completed';

    return (
      <div className="min-h-screen" style={{ backgroundColor: '#F6F1E8' }}>
        <div className="max-w-2xl mx-auto px-4 sm:px-6 py-8">
          {isConfirmed ? (
            /* ── Confirmed ── */
            <div className="bg-[#6EE7B7]/20 border-[3px] border-[#121212] shadow-[6px_6px_0px_0px_#121212] rounded-2xl p-8 md:p-12 text-center" data-testid="payment-confirmed-card">
              <CheckCircle2 className="w-16 h-16 mx-auto mb-6" strokeWidth={2} style={{ color: '#16a34a' }} />
              <h1 className="text-2xl md:text-3xl font-black uppercase tracking-tight mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Payment Confirmed!
              </h1>
              <p className="text-base font-medium mb-8" style={{ color: '#555' }}>
                Your <span className="font-bold uppercase">{paymentMethod}</span> payment of{' '}
                <span className="font-mono font-bold" style={{ fontFamily: 'JetBrains Mono, monospace' }}>${totalCost}</span>{' '}
                has been verified. You're all set!
              </p>
              <button
                onClick={() => navigate('/dashboard/startup')}
                className="bg-[#EDE3C7] text-[#121212] font-black uppercase text-sm border-[3px] border-[#121212] shadow-[4px_4px_0px_0px_#121212] hover:-translate-y-[2px] hover:shadow-[6px_6px_0px_0px_#121212] active:translate-y-[4px] active:shadow-none transition-all rounded-xl px-6 py-3"
                data-testid="back-dashboard-btn"
              >
                <ArrowLeft className="w-4 h-4 inline mr-2 -mt-0.5" strokeWidth={3} />
                Dashboard
              </button>
            </div>
          ) : (
            /* ── Pending verification ── */
            <div className="bg-[#E8C86A]/20 border-[3px] border-[#121212] shadow-[6px_6px_0px_0px_#121212] rounded-2xl p-8 md:p-12 text-center" data-testid="payment-pending-card">
              <div className="text-5xl mb-6">⏳</div>
              <h1 className="text-2xl md:text-3xl font-black uppercase tracking-tight mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Payment Received!
              </h1>
              <div className="bg-[#EDE3C7] border-[3px] border-[#121212] shadow-[3px_3px_0px_0px_#121212] rounded-xl p-5 mb-6 text-left">
                <p className="text-sm font-medium mb-3" style={{ color: '#555' }}>
                  Your <span className="font-bold uppercase">{paymentMethod}</span> payment of{' '}
                  <span className="font-mono font-bold" style={{ fontFamily: 'JetBrains Mono, monospace' }}>${totalCost}</span>{' '}
                  has been recorded. Now we just need to verify it manually.
                </p>
                <p className="text-sm font-bold" style={{ color: '#121212' }}>
                  🔥 Real talk — this is literally a 2-person operation. No webhooks, no automation, just two founders
                  and a shared screen. We'll confirm your payment within ~30 minutes (faster if we're not eating).
                </p>
              </div>
              <div className="flex items-center justify-center gap-2 mb-6">
                <Clock className="w-4 h-4" strokeWidth={2.5} style={{ color: '#E8C86A' }} />
                <span className="text-xs font-black uppercase tracking-wider" style={{ color: '#888' }}>
                  ETA: ~30 minutes · Status: Pending Verification
                </span>
              </div>
              <p className="text-xs font-medium mb-8" style={{ color: '#888' }}>
                You'll get an email the moment we verify. Pinky promise. 🤙
              </p>
              <button
                onClick={() => navigate('/dashboard/startup')}
                className="bg-[#EDE3C7] text-[#121212] font-black uppercase text-sm border-[3px] border-[#121212] shadow-[4px_4px_0px_0px_#121212] hover:-translate-y-[2px] hover:shadow-[6px_6px_0px_0px_#121212] active:translate-y-[4px] active:shadow-none transition-all rounded-xl px-6 py-3"
                data-testid="back-dashboard-btn"
              >
                <ArrowLeft className="w-4 h-4 inline mr-2 -mt-0.5" strokeWidth={3} />
                Back to Dashboard
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  // No acceptances
  if (acceptances.length === 0) {
    return (
      <div className="min-h-screen" style={{ backgroundColor: '#F6F1E8' }}>
        <div className="max-w-2xl mx-auto px-4 sm:px-6 py-8">
          {/* Back */}
          <button
            onClick={() => navigate('/dashboard/startup')}
            className="flex items-center gap-2 text-sm font-bold mb-6 hover:text-[#FF6B6B] transition-colors"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-4 h-4" strokeWidth={3} /> Back to Dashboard
          </button>
          <div className="bg-[#EDE3C7] border-[3px] border-[#121212] shadow-[6px_6px_0px_0px_#121212] rounded-2xl p-8 md:p-12 text-center">
            <ShoppingCart className="w-12 h-12 mx-auto mb-4" strokeWidth={1.5} style={{ color: '#aaa' }} />
            <h2 className="text-xl font-black uppercase mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>No Accepted Students Yet</h2>
            <p className="text-sm font-medium" style={{ color: '#888' }}>Accept student bids from the market first, then come back to checkout.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#F6F1E8' }}>
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8">
        {/* Back */}
        <button
          onClick={() => navigate('/dashboard/startup')}
          className="flex items-center gap-2 text-sm font-bold mb-6 hover:text-[#FF6B6B] transition-colors"
          data-testid="back-btn"
        >
          <ArrowLeft className="w-4 h-4" strokeWidth={3} /> Back to Dashboard
        </button>

        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          {checkoutData?.logo_url && (
            <div className="w-14 h-14 rounded-2xl border-[3px] border-[#121212] shadow-[3px_3px_0px_0px_#121212] overflow-hidden flex-shrink-0">
              <img src={getLogoUrl(checkoutData.logo_url)} alt="Logo" className="w-full h-full object-cover" />
            </div>
          )}
          <div>
            <h1 className="text-2xl md:text-3xl font-black uppercase tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="checkout-title">
              Checkout
            </h1>
            <p className="text-sm font-medium" style={{ color: '#888' }}>
              {checkoutData?.startup_name} · {acceptances.length} student{acceptances.length !== 1 ? 's' : ''}
            </p>
          </div>
        </div>

        {/* Expiry Countdown Banner */}
        <CheckoutExpiryBanner acceptances={acceptances} expiryCountdown={expiryCountdown} setExpiryCountdown={setExpiryCountdown} expiryRef={expiryRef} />

        {/* Order Summary */}
        <div className="bg-[#EDE3C7] border-[3px] border-[#121212] shadow-[6px_6px_0px_0px_#121212] rounded-2xl mb-8" data-testid="order-summary">
          <div className="bg-[#121212] text-[#F6F1E8] px-5 py-3 flex items-center gap-2 rounded-t-xl">
            <ShoppingCart className="w-4 h-4" strokeWidth={3} />
            <h2 className="font-black uppercase text-sm tracking-wider">Order Summary</h2>
          </div>

          {/* Line items */}
          <div className="divide-y divide-[#121212]/10">
            {acceptances.map((a, i) => (
              <div key={a.acceptance_id} className="flex items-center gap-3 px-5 py-3" data-testid={`checkout-item-${i}`}>
                <div
                  className="w-9 h-9 rounded-xl border-[2px] border-[#121212] flex items-center justify-center font-black text-xs flex-shrink-0"
                  style={{ backgroundColor: a.avatar_color || '#E8C86A' }}
                >
                  {(a.wearer_name || 'M')[0]}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-sm truncate">{a.wearer_name || 'Mystery Student'}</div>
                  <div className="text-xs font-medium" style={{ color: '#888' }}>
                    {a.garment_type || 'tee'} · {a.status || 'pending'} · {getTimeAgo(a.created_at)}
                  </div>
                </div>
                <div className="font-mono text-lg font-bold flex-shrink-0" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                  ${a.asking_price}
                </div>
              </div>
            ))}
          </div>

          {/* Platform Fee */}
          {platformFee > 0 && (
            <div className="border-t-[2px] border-[#121212]/10 px-5 py-3 flex items-center justify-between" style={{ overflow: 'visible' }}>
              <div className="flex items-center gap-1.5">
                <span className="font-bold text-sm">Platform Fee</span>
                <div className="relative group" style={{ overflow: 'visible' }}>
                  <Info className="w-3.5 h-3.5 cursor-help" strokeWidth={2.5} style={{ color: '#888' }} data-testid="platform-fee-info" />
                  <div className="absolute left-0 mb-2 px-3 py-2 bg-[#121212] text-[#F6F1E8] text-xs font-medium rounded-lg shadow-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-50" style={{ bottom: '100%' }}>
                    This fee helps us cover shipping & logistics costs
                    <div className="absolute top-full left-[6px] w-0 h-0 border-l-[5px] border-l-transparent border-r-[5px] border-r-transparent border-t-[5px] border-t-[#121212]" />
                  </div>
                </div>
              </div>
              <span className="font-mono text-sm font-bold" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                +${platformFee}
              </span>
            </div>
          )}

          {/* Total */}
          <div className="border-t-[3px] border-[#121212] px-5 py-4 flex items-center justify-between bg-[#E8C86A]/30 rounded-b-xl">
            <span className="font-black uppercase text-sm tracking-wider">Total</span>
            <span className="font-mono text-2xl font-bold" style={{ fontFamily: 'JetBrains Mono, monospace' }} data-testid="checkout-total">
              ${grandTotal}
            </span>
          </div>
        </div>

        {/* Country Toggle */}
        {isIndia === null ? (
          <div className="bg-[#EDE3C7] border-[3px] border-[#121212] shadow-[6px_6px_0px_0px_#121212] rounded-2xl p-6 md:p-8 mb-8" data-testid="country-selector">
            <h2 className="text-lg font-black uppercase tracking-tight mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
              <Globe className="w-5 h-5 inline mr-2 -mt-0.5" strokeWidth={2.5} />
              Where are you based?
            </h2>
            <p className="text-sm font-medium mb-6" style={{ color: '#888' }}>
              This determines your available payment methods.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <button
                onClick={() => setIsIndia(true)}
                className="flex-1 bg-[#FF6B6B] text-[#121212] font-black uppercase text-base border-[3px] border-[#121212] shadow-[4px_4px_0px_0px_#121212] hover:-translate-y-[2px] hover:shadow-[6px_6px_0px_0px_#121212] active:translate-y-[4px] active:shadow-none transition-all rounded-xl px-6 py-4 flex items-center justify-center gap-3"
                data-testid="country-india-btn"
              >
                <IndianRupee className="w-5 h-5" strokeWidth={3} />
                India
              </button>
              <button
                onClick={() => setIsIndia(false)}
                className="flex-1 bg-[#D8DFEA] text-[#121212] font-black uppercase text-base border-[3px] border-[#121212] shadow-[4px_4px_0px_0px_#121212] hover:-translate-y-[2px] hover:shadow-[6px_6px_0px_0px_#121212] active:translate-y-[4px] active:shadow-none transition-all rounded-xl px-6 py-4 flex items-center justify-center gap-3"
                data-testid="country-international-btn"
              >
                <Globe className="w-5 h-5" strokeWidth={3} />
                International
              </button>
            </div>
          </div>
        ) : (
          <>
            {/* Country selected — show toggle to switch */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                {isIndia ? (
                  <span className="bg-[#FF6B6B] text-[#121212] text-xs font-black uppercase border-[2px] border-[#121212] rounded-full px-3 py-1 shadow-[2px_2px_0px_0px_#121212]">
                    <IndianRupee className="w-3 h-3 inline -mt-0.5 mr-1" strokeWidth={3} />India
                  </span>
                ) : (
                  <span className="bg-[#D8DFEA] text-[#121212] text-xs font-black uppercase border-[2px] border-[#121212] rounded-full px-3 py-1 shadow-[2px_2px_0px_0px_#121212]">
                    <Globe className="w-3 h-3 inline -mt-0.5 mr-1" strokeWidth={3} />International
                  </span>
                )}
              </div>
              <button
                onClick={() => { setIsIndia(null); setPaymentMethod(null); }}
                className="text-xs font-bold underline hover:text-[#FF6B6B] transition-colors"
                data-testid="switch-country-btn"
              >
                Change
              </button>
            </div>

            {/* Payment Panel */}
            {isIndia ? (
              /* ───── INDIA: Razorpay ───── */
              <div className="bg-[#EDE3C7] border-[3px] border-[#121212] shadow-[6px_6px_0px_0px_#121212] rounded-2xl overflow-hidden mb-8" data-testid="payment-india">
                <div className="bg-[#121212] text-[#F6F1E8] px-5 py-3 flex items-center gap-2">
                  <CreditCard className="w-4 h-4" strokeWidth={3} />
                  <h2 className="font-black uppercase text-sm tracking-wider">Pay via Razorpay</h2>
                </div>
                <div className="p-6">
                  <p className="text-sm font-medium mb-6" style={{ color: '#555' }}>
                    Click below to complete your payment of{' '}
                    <span className="font-mono font-bold" style={{ fontFamily: 'JetBrains Mono, monospace' }}>₹{inrGrandTotal.toLocaleString('en-IN')}</span>{' '}
                    <span className="text-xs" style={{ color: '#888' }}>(${grandTotal} × ₹{usdToInr.toFixed(2)})</span>{' '}
                    via UPI, Cards, or Netbanking.
                  </p>
                  <a
                    href={`${PAYMENT_CONFIG.razorpay.link}?amount=${inrGrandTotal}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block w-full bg-[#FF6B6B] text-[#121212] font-black uppercase text-center border-[3px] border-[#121212] shadow-[4px_4px_0px_0px_#121212] hover:-translate-y-[2px] hover:shadow-[6px_6px_0px_0px_#121212] active:translate-y-[4px] active:shadow-none transition-all rounded-xl px-6 py-4 text-lg mb-4"
                    data-testid="razorpay-pay-btn"
                  >
                    <CreditCard className="w-5 h-5 inline mr-2 -mt-0.5" strokeWidth={3} />
                    Pay ₹{inrGrandTotal.toLocaleString('en-IN')} via Razorpay
                    <ExternalLink className="w-4 h-4 inline ml-2 -mt-0.5" strokeWidth={3} />
                  </a>
                  <p className="text-xs font-medium text-center" style={{ color: '#888' }}>
                    After completing payment, click "I've Paid" below.
                  </p>
                </div>
                <div className="border-t-[3px] border-[#121212] p-5">
                  <button
                    onClick={() => handleSubmitPayment('razorpay')}
                    disabled={submitting}
                    className="w-full bg-[#6EE7B7] text-[#121212] font-black uppercase border-[3px] border-[#121212] shadow-[4px_4px_0px_0px_#121212] hover:-translate-y-[2px] hover:shadow-[6px_6px_0px_0px_#121212] active:translate-y-[4px] active:shadow-none transition-all rounded-xl px-6 py-3 disabled:opacity-50"
                    data-testid="confirm-razorpay-btn"
                  >
                    {submitting ? 'Submitting...' : "✓ I've Completed Payment"}
                  </button>
                </div>
              </div>
            ) : (
              /* ───── INTERNATIONAL: Venmo / PayPal (toggle) ───── */
              <div className="mb-8">
                <div className="bg-[#121212] text-[#F6F1E8] border-[3px] border-[#121212] shadow-[4px_4px_0px_0px_#E8C86A] rounded-xl px-5 py-3 mb-6 text-xs font-medium" style={{ lineHeight: '1.6' }}>
                  <span className="font-black uppercase text-[#E8C86A]">Heads up —</span>{' '}
                  Our international payment gateway didn't get approved in time, so we built this checkout in a day. Scan the QR, send the amount, and hit confirm. Old school, but it works. 🤝
                </div>
                {/* Toggle Tabs */}
                <div className="flex border-[3px] border-[#121212] rounded-xl overflow-hidden shadow-[4px_4px_0px_0px_#121212] mb-6" data-testid="payment-toggle">
                  <button
                    onClick={() => setPaymentTab('venmo')}
                    className={`flex-1 py-3 font-black uppercase text-xs sm:text-sm tracking-wider transition-all flex items-center justify-center gap-1.5 ${paymentTab === 'venmo'
                      ? 'bg-[#121212] text-[#F6F1E8]'
                      : 'bg-[#EDE3C7] text-[#121212] hover:bg-[#E8C86A]/30'
                      }`}
                    data-testid="tab-venmo-btn"
                  >
                    <CreditCard className="w-3.5 h-3.5" strokeWidth={3} />
                    Venmo
                  </button>
                  <button
                    onClick={() => setPaymentTab('paypal')}
                    className={`flex-1 py-3 font-black uppercase text-xs sm:text-sm tracking-wider transition-all flex items-center justify-center gap-1.5 border-l-[3px] border-[#121212] ${paymentTab === 'paypal'
                      ? 'bg-[#121212] text-[#F6F1E8]'
                      : 'bg-[#EDE3C7] text-[#121212] hover:bg-[#E8C86A]/30'
                      }`}
                    data-testid="tab-paypal-btn"
                  >
                    <CreditCard className="w-3.5 h-3.5" strokeWidth={3} />
                    PayPal
                  </button>
                  <button
                    onClick={() => setPaymentTab('zelle')}
                    className={`flex-1 py-3 font-black uppercase text-xs sm:text-sm tracking-wider transition-all flex items-center justify-center gap-1.5 border-l-[3px] border-[#121212] ${paymentTab === 'zelle'
                      ? 'bg-[#121212] text-[#F6F1E8]'
                      : 'bg-[#EDE3C7] text-[#121212] hover:bg-[#E8C86A]/30'
                      }`}
                    data-testid="tab-zelle-btn"
                  >
                    <CreditCard className="w-3.5 h-3.5" strokeWidth={3} />
                    Zelle
                  </button>
                </div>

                {/* Venmo Panel */}
                {paymentTab === 'venmo' && (
                  <div className="bg-[#EDE3C7] border-[3px] border-[#121212] shadow-[6px_6px_0px_0px_#121212] rounded-2xl overflow-hidden" data-testid="payment-venmo">
                    <div className="bg-[#121212] text-[#F6F1E8] px-5 py-3 flex items-center gap-2">
                      <CreditCard className="w-4 h-4" strokeWidth={3} />
                      <h2 className="font-black uppercase text-sm tracking-wider">Pay via Venmo</h2>
                    </div>
                    <div className="p-6">
                      <p className="text-sm font-medium mb-4" style={{ color: '#555' }}>
                        Scan the QR code below to send{' '}
                        <span className="font-mono font-bold" style={{ fontFamily: 'JetBrains Mono, monospace' }}>${grandTotal}</span>{' '}
                        via Venmo:
                      </p>
                      <div className="flex justify-center mb-4">
                        <div
                          className="bg-white border-[3px] border-[#121212] shadow-[4px_4px_0px_0px_#121212] rounded-2xl p-3 inline-block cursor-pointer hover:-translate-y-[2px] hover:shadow-[6px_6px_0px_0px_#121212] transition-all"
                          onClick={() => setLightboxSrc('/assets/venmo.jpeg')}
                        >
                          <img
                            src="/assets/venmo.jpeg"
                            alt="Venmo QR Code"
                            className="w-56 h-56 sm:w-64 sm:h-64 object-contain rounded-lg"
                            data-testid="venmo-qr-img"
                          />
                        </div>
                      </div>
                      <button
                        onClick={() => setLightboxSrc('/assets/venmo.jpeg')}
                        className="flex items-center justify-center gap-1.5 text-xs font-bold mb-4 hover:text-[#FF6B6B] transition-colors mx-auto"
                        data-testid="enlarge-venmo-btn"
                      >
                        <Maximize2 className="w-3.5 h-3.5" strokeWidth={2.5} />
                        Tap to enlarge QR code
                      </button>

                      <p className="text-xs font-medium" style={{ color: '#888' }}>
                        Include your startup name "<span className="font-bold">{checkoutData?.startup_name}</span>" in the payment note.
                      </p>
                    </div>
                    <div className="border-t-[3px] border-[#121212] p-5">
                      <button
                        onClick={() => handleSubmitPayment('venmo')}
                        disabled={submitting}
                        className="w-full bg-[#6EE7B7] text-[#121212] font-black uppercase border-[3px] border-[#121212] shadow-[4px_4px_0px_0px_#121212] hover:-translate-y-[2px] hover:shadow-[6px_6px_0px_0px_#121212] active:translate-y-[4px] active:shadow-none transition-all rounded-xl px-6 py-3 disabled:opacity-50"
                        data-testid="confirm-venmo-btn"
                      >
                        {submitting ? 'Submitting...' : "✓ I've Sent via Venmo"}
                      </button>
                    </div>
                  </div>
                )}

                {/* PayPal Panel */}
                {paymentTab === 'paypal' && (
                  <div className="bg-[#EDE3C7] border-[3px] border-[#121212] shadow-[6px_6px_0px_0px_#121212] rounded-2xl overflow-hidden" data-testid="payment-paypal">
                    <div className="bg-[#121212] text-[#F6F1E8] px-5 py-3 flex items-center gap-2">
                      <CreditCard className="w-4 h-4" strokeWidth={3} />
                      <h2 className="font-black uppercase text-sm tracking-wider">Pay via PayPal</h2>
                    </div>
                    <div className="p-6">
                      <p className="text-sm font-medium mb-4" style={{ color: '#555' }}>
                        Scan the QR code or send{' '}
                        <span className="font-mono font-bold" style={{ fontFamily: 'JetBrains Mono, monospace' }}>${grandTotal}</span> via PayPal:
                      </p>
                      <div className="flex justify-center mb-4">
                        <div
                          className="bg-white border-[3px] border-[#121212] shadow-[4px_4px_0px_0px_#121212] rounded-2xl p-3 inline-block cursor-pointer hover:-translate-y-[2px] hover:shadow-[6px_6px_0px_0px_#121212] transition-all"
                          onClick={() => setLightboxSrc('/assets/paypal.jpeg')}
                        >
                          <img
                            src="/assets/paypal.jpeg"
                            alt="PayPal QR Code"
                            className="w-56 h-56 sm:w-64 sm:h-64 object-contain rounded-lg"
                            data-testid="paypal-qr-img"
                          />
                        </div>
                      </div>
                      <button
                        onClick={() => setLightboxSrc('/assets/paypal.jpeg')}
                        className="flex items-center justify-center gap-1.5 text-xs font-bold mb-4 hover:text-[#FF6B6B] transition-colors mx-auto"
                        data-testid="enlarge-paypal-btn"
                      >
                        <Maximize2 className="w-3.5 h-3.5" strokeWidth={2.5} />
                        Tap to enlarge QR code
                      </button>

                      <p className="text-xs font-medium mt-3" style={{ color: '#888' }}>
                        Include your startup name "<span className="font-bold">{checkoutData?.startup_name}</span>" in the payment note.
                      </p>
                    </div>
                    <div className="border-t-[3px] border-[#121212] p-5">
                      <button
                        onClick={() => handleSubmitPayment('paypal')}
                        disabled={submitting}
                        className="w-full bg-[#6EE7B7] text-[#121212] font-black uppercase border-[3px] border-[#121212] shadow-[4px_4px_0px_0px_#121212] hover:-translate-y-[2px] hover:shadow-[6px_6px_0px_0px_#121212] active:translate-y-[4px] active:shadow-none transition-all rounded-xl px-6 py-3 disabled:opacity-50"
                        data-testid="confirm-paypal-btn"
                      >
                        {submitting ? 'Submitting...' : "✓ I've Sent via PayPal"}
                      </button>
                    </div>
                  </div>
                )}

                {/* Zelle Panel */}
                {paymentTab === 'zelle' && (
                  <div className="bg-[#EDE3C7] border-[3px] border-[#121212] shadow-[6px_6px_0px_0px_#121212] rounded-2xl overflow-hidden" data-testid="payment-zelle">
                    <div className="bg-[#121212] text-[#F6F1E8] px-5 py-3 flex items-center gap-2">
                      <CreditCard className="w-4 h-4" strokeWidth={3} />
                      <h2 className="font-black uppercase text-sm tracking-wider">Pay via Zelle</h2>
                    </div>
                    <div className="p-6">
                      <p className="text-sm font-medium mb-4" style={{ color: '#555' }}>
                        Scan the QR code below to send{' '}
                        <span className="font-mono font-bold" style={{ fontFamily: 'JetBrains Mono, monospace' }}>${grandTotal}</span>{' '}
                        via Zelle:
                      </p>
                      <div className="flex justify-center mb-4">
                        <div
                          className="bg-white border-[3px] border-[#121212] shadow-[4px_4px_0px_0px_#121212] rounded-2xl p-3 inline-block cursor-pointer hover:-translate-y-[2px] hover:shadow-[6px_6px_0px_0px_#121212] transition-all"
                          onClick={() => setLightboxSrc('/assets/Zelle.jpeg')}
                        >
                          <img
                            src="/assets/Zelle.jpeg"
                            alt="Zelle QR Code"
                            className="w-56 h-56 sm:w-64 sm:h-64 object-contain rounded-lg"
                            data-testid="zelle-qr-img"
                          />
                        </div>
                      </div>
                      <button
                        onClick={() => setLightboxSrc('/assets/Zelle.jpeg')}
                        className="flex items-center justify-center gap-1.5 text-xs font-bold mb-4 hover:text-[#FF6B6B] transition-colors mx-auto"
                        data-testid="enlarge-zelle-btn"
                      >
                        <Maximize2 className="w-3.5 h-3.5" strokeWidth={2.5} />
                        Tap to enlarge QR code
                      </button>
                      <p className="text-xs font-medium" style={{ color: '#888' }}>
                        Include your startup name "<span className="font-bold">{checkoutData?.startup_name}</span>" in the payment note.
                      </p>
                    </div>
                    <div className="border-t-[3px] border-[#121212] p-5">
                      <button
                        onClick={() => handleSubmitPayment('zelle')}
                        disabled={submitting}
                        className="w-full bg-[#6EE7B7] text-[#121212] font-black uppercase border-[3px] border-[#121212] shadow-[4px_4px_0px_0px_#121212] hover:-translate-y-[2px] hover:shadow-[6px_6px_0px_0px_#121212] active:translate-y-[4px] active:shadow-none transition-all rounded-xl px-6 py-3 disabled:opacity-50"
                        data-testid="confirm-zelle-btn"
                      >
                        {submitting ? 'Submitting...' : "✓ I've Sent via Zelle"}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
      {/* QR Lightbox */}
      {lightboxSrc && (
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/70 backdrop-blur-sm"
          onClick={() => setLightboxSrc(null)}
          data-testid="qr-lightbox"
        >
          <div className="relative" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={() => setLightboxSrc(null)}
              className="absolute -top-4 -right-4 bg-[#FF6B6B] text-[#121212] border-[3px] border-[#121212] shadow-[3px_3px_0px_0px_#121212] rounded-full w-10 h-10 flex items-center justify-center font-black hover:-translate-y-[1px] hover:shadow-[4px_4px_0px_0px_#121212] active:translate-y-[2px] active:shadow-none transition-all z-10"
              data-testid="close-lightbox-btn"
            >
              <X className="w-5 h-5" strokeWidth={3} />
            </button>
            <div className="bg-white border-[4px] border-[#121212] shadow-[8px_8px_0px_0px_#121212] rounded-2xl p-4">
              <img
                src={lightboxSrc}
                alt="QR Code"
                className="w-[80vw] max-w-[400px] h-auto object-contain rounded-lg"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function CheckoutExpiryBanner({ acceptances, expiryCountdown, setExpiryCountdown, expiryRef }) {
  useEffect(() => {
    if (expiryRef.current) {
      clearInterval(expiryRef.current);
      expiryRef.current = null;
    }

    // Find the earliest expiring pending acceptance
    const pendingWithExpiry = (acceptances || []).filter(a => a.status === 'pending' && a.expires_at);
    if (pendingWithExpiry.length === 0) {
      setExpiryCountdown(null);
      return;
    }

    const earliestExpiry = Math.min(...pendingWithExpiry.map(a => new Date(a.expires_at).getTime()));

    const tick = () => {
      const now = Date.now();
      const diff = earliestExpiry - now;
      if (diff <= 0) {
        setExpiryCountdown({ mins: 0, secs: 0, expired: true });
        if (expiryRef.current) clearInterval(expiryRef.current);
        return;
      }
      const mins = Math.floor(diff / 60000);
      const secs = Math.floor((diff % 60000) / 1000);
      setExpiryCountdown({ mins, secs, expired: false });
    };

    tick();
    expiryRef.current = setInterval(tick, 1000);
    return () => { if (expiryRef.current) clearInterval(expiryRef.current); };
  }, [acceptances, setExpiryCountdown, expiryRef]);

  if (!expiryCountdown) return null;

  if (expiryCountdown.expired) {
    return (
      <div className="bg-[#FF6B6B]/20 border-[3px] border-[#FF6B6B] shadow-[4px_4px_0px_0px_#121212] rounded-2xl p-4 mb-6 text-center" data-testid="expiry-expired-banner">
        <AlertCircle className="w-6 h-6 mx-auto mb-2" strokeWidth={2.5} style={{ color: '#FF6B6B' }} />
        <p className="font-black uppercase text-sm mb-1" style={{ color: '#FF6B6B' }}>Time's Up</p>
        <p className="text-xs font-medium" style={{ color: '#888' }}>
          Some acceptances have expired and the asks were released back to the market.
        </p>
      </div>
    );
  }

  const isUrgent = (expiryCountdown.mins * 60 + expiryCountdown.secs) < 120;

  return (
    <div
      className={`border-[3px] shadow-[4px_4px_0px_0px_#121212] rounded-2xl p-4 mb-6 flex items-center justify-between ${isUrgent ? 'border-[#FF6B6B] bg-[#FF6B6B]/10 animate-pulse' : 'border-[#E8C86A] bg-[#E8C86A]/10'}`}
      data-testid="expiry-countdown-banner"
    >
      <div className="flex items-center gap-3">
        <Clock className="w-5 h-5" strokeWidth={2.5} style={{ color: isUrgent ? '#FF6B6B' : '#E8C86A' }} />
        <div>
          <p className="font-black uppercase text-xs tracking-wider" style={{ color: isUrgent ? '#FF6B6B' : '#121212' }}>
            Complete payment before time runs out
          </p>
          <p className="text-xs font-medium mt-0.5" style={{ color: '#888' }}>
            Accepted asks will be released back to the market when the timer expires
          </p>
        </div>
      </div>
      <div
        className="font-mono text-2xl font-bold flex-shrink-0 ml-4"
        style={{ fontFamily: 'JetBrains Mono, monospace', color: isUrgent ? '#FF6B6B' : '#121212' }}
      >
        {String(expiryCountdown.mins).padStart(2, '0')}:{String(expiryCountdown.secs).padStart(2, '0')}
      </div>
    </div>
  );
}
